import { CAR_MODELS, DEFAULT_CAR_MODEL_ID, findCarModelById, hasInteriorView } from '../data/carModels';
import { DEFAULT_STUDIO_ID, findStudioById, studioOptions } from '../data/studioOptions';
import { DEFAULT_WEATHER_ID, findWeatherById, weatherOptions } from '../data/weatherOptions';
import { paintOptions } from '../data/paintOptions';
import { wheelOptions } from '../data/wheelOptions';
import { interiorOptions } from '../data/interiorOptions';
import type { CatalogOption, Configuration, PriceableSelection, SelectedOptions } from '../types';

export const CONFIGURATION_ACTION_TYPES = {
  SELECT_MODEL: 'SELECT_MODEL',
  SELECT_STUDIO: 'SELECT_STUDIO',
  SELECT_WEATHER: 'SELECT_WEATHER',
  SELECT_PAINT: 'SELECT_PAINT',
  SELECT_WHEEL: 'SELECT_WHEEL',
  SELECT_INTERIOR: 'SELECT_INTERIOR',
} as const;

export type ConfigurationActionType =
  (typeof CONFIGURATION_ACTION_TYPES)[keyof typeof CONFIGURATION_ACTION_TYPES];

/** 선택 액션은 모두 같은 optionId 형태라 종류별 인터페이스로 반복하지 않습니다. */
export interface ConfigurationAction {
  type: ConfigurationActionType;
  optionId: string;
}

export function createDefaultConfiguration(): Configuration {
  return {
    modelId: DEFAULT_CAR_MODEL_ID,
    studioId: DEFAULT_STUDIO_ID,
    weatherId: DEFAULT_WEATHER_ID,
    paintId: paintOptions[0].id,
    wheelId: wheelOptions[0].id,
    interiorId: interiorOptions[0].id,
  };
}

function findOptionById<T extends CatalogOption>(options: readonly T[], optionId: string): T | undefined {
  return options.find((option) => option.id === optionId);
}

function hasOption(options: readonly CatalogOption[], optionId: string): boolean {
  return findOptionById(options, optionId) !== undefined;
}

/** 차량 사양과 뷰어 설정은 모델을 바꿔도 사용자가 고른 값을 유지합니다. */
export function configurationReducer(
  configuration: Configuration,
  action: ConfigurationAction
): Configuration {
  switch (action.type) {
    case CONFIGURATION_ACTION_TYPES.SELECT_MODEL:
      if (findCarModelById(action.optionId) === undefined) {
        return configuration;
      }
      return { ...configuration, modelId: action.optionId };
    case CONFIGURATION_ACTION_TYPES.SELECT_STUDIO:
      if (findStudioById(action.optionId) === undefined) {
        return configuration;
      }
      return { ...configuration, studioId: action.optionId };
    case CONFIGURATION_ACTION_TYPES.SELECT_WEATHER:
      if (findWeatherById(action.optionId) === undefined) {
        return configuration;
      }
      return { ...configuration, weatherId: action.optionId };
    case CONFIGURATION_ACTION_TYPES.SELECT_PAINT:
      if (!hasOption(paintOptions, action.optionId)) {
        return configuration;
      }
      return { ...configuration, paintId: action.optionId };
    case CONFIGURATION_ACTION_TYPES.SELECT_WHEEL:
      if (!hasOption(wheelOptions, action.optionId)) {
        return configuration;
      }
      return { ...configuration, wheelId: action.optionId };
    case CONFIGURATION_ACTION_TYPES.SELECT_INTERIOR:
      if (!hasOption(interiorOptions, action.optionId)) {
        return configuration;
      }
      return { ...configuration, interiorId: action.optionId };
    default:
      return configuration;
  }
}

/** 검증된 id를 실제 카탈로그 객체로 바꿉니다. */
export function resolveSelectedOptions(configuration: Configuration): SelectedOptions {
  return {
    model: findCarModelById(configuration.modelId)!,
    studio: findStudioById(configuration.studioId)!,
    weather: findWeatherById(configuration.weatherId)!,
    paint: findOptionById(paintOptions, configuration.paintId)!,
    wheel: findOptionById(wheelOptions, configuration.wheelId)!,
    interior: findOptionById(interiorOptions, configuration.interiorId)!,
  };
}

/** 배경과 날씨는 차량 사양이 아니라 뷰어 설정이므로 가격에 들어가지 않습니다. */
export function calculateOptionsPrice(selectedOptions: PriceableSelection): number {
  const interiorPrice = hasInteriorView(selectedOptions.model) ? selectedOptions.interior.price : 0;
  return selectedOptions.paint.price + selectedOptions.wheel.price + interiorPrice;
}

export function calculateTotalPrice(selectedOptions: PriceableSelection): number {
  return selectedOptions.model.basePrice + calculateOptionsPrice(selectedOptions);
}

const koreanWonFormatter = new Intl.NumberFormat('ko-KR');

export function formatKoreanWon(amount: number): string {
  return `₩${koreanWonFormatter.format(amount)}`;
}

// 카탈로그를 한 번에 넘기고 싶은 소비자를 위해 재노출합니다.
export { CAR_MODELS, studioOptions, weatherOptions };
